/**
 * BookmarkletToolPage — the reusable "bookmarklet install" tool-detail page,
 * recreated from the 2026-05-19 welcome prototype (v37) template:
 * Purpose → Install → How to use → (optional) Training. Per Jared (v36) each
 * bookmarklet co-locates with its own tool page — there is NO generic
 * "Bookmarklets" list — so each is a static install page driven by config.
 *
 * Content-only page: the authenticated shell is provided by `WebShellLayout`.
 *
 * Bookmarklet install mechanic: a bookmarklet is a `javascript:` URL the user
 * drags onto their bookmarks bar. React refuses to render a `javascript:` href
 * via the JSX `href` prop (it sanitizes it), so we set it on the DOM node with
 * a ref — that keeps the chip draggable/installable while bypassing React's
 * sanitizer. Clicking the chip in-app is guarded (it only runs from the
 * bookmarks bar, on the tool's target page) and shows an install hint instead.
 *
 * Consumers: `RocketReachTool`.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Bookmark, Play } from "lucide-react";
import { toast } from "sonner";
import { useMe } from "@nu-assist/shared-fe";

export interface BookmarkletToolPageProps {
  /** Stable testid for the root (matches the leftMenuModel item id). */
  testId: string;
  /** Breadcrumb section label, e.g. "Research & Sourcing". */
  breadcrumbSection: string;
  /** Tool name — used for the heading, breadcrumb leaf, and chip label. */
  title: string;
  /** One-line purpose under the heading. */
  subtitle: string;
  /** "What it does" body copy. */
  whatItDoes: ReactNode;
  /** The verbatim `javascript:` bookmarklet script. */
  bookmarklet: string;
  /** Toast shown if the chip is clicked in-app (it only runs from the bar). */
  inAppClickHint: string;
  /** "How to use" steps (stable `id` per step for the list key). */
  howToSteps: { id: string; content: ReactNode }[];
  /** Optional training video link. */
  training?: { url: string; label: string; note?: string };
}

export function BookmarkletToolPage({
  testId,
  breadcrumbSection,
  title,
  subtitle,
  whatItDoes,
  bookmarklet,
  inAppClickHint,
  howToSteps,
  training,
}: BookmarkletToolPageProps) {
  const me = useMe();
  const chipRef = useRef<HTMLAnchorElement>(null);

  // Set the `javascript:` href on the DOM node (React would otherwise strip it).
  // This is what makes the chip install as a working bookmarklet on drag.
  useEffect(() => {
    chipRef.current?.setAttribute("href", bookmarklet);
  }, [bookmarklet]);

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  return (
    <div data-testid={testId} className="p-12">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <nav aria-label="Breadcrumb" className="mb-2 text-caption text-text-secondary">
          {breadcrumbSection} <span className="px-1">/</span>
          <span className="text-accent">{title}</span>
        </nav>
        <h1 className="mb-2 text-h1 text-text">{title}</h1>
        <p className="mb-6 text-body text-text-secondary">{subtitle}</p>

        <div className="flex flex-col gap-5">
          {/* What it does */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">What it does</h2>
            <p className="text-body text-text-secondary">{whatItDoes}</p>
          </section>

          {/* Install */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">Install</h2>
            <p className="text-body text-text-secondary">
              This tool is a <strong className="font-semibold text-text">bookmarklet</strong> that goes in your bookmarks bar.
            </p>
            <div className="my-4 rounded-lg border-2 border-dashed border-border-hover bg-accent-muted p-6 text-center">
              <span className="mb-4 block text-caption text-text-secondary">
                ↳ Drag this button up to your bookmarks bar
              </span>
              {/* biome-ignore lint/a11y/useValidAnchor: a bookmarklet MUST be a draggable <a> carrying a javascript: URL (set via the ref above) — a <button> can't be dragged to the bookmarks bar to install; the in-app click is guarded (preventDefault + toast). */}
              <a
                ref={chipRef}
                href="#install"
                draggable
                title="Bookmarklet — drag me to your bookmarks bar"
                onClick={(e) => {
                  // Guards left-click + Enter from executing the bookmarklet in
                  // the app. A context-menu "Open link" / middle-click can still
                  // navigate the javascript: href here — but that's harmless:
                  // each tool's script targets its own external page (a
                  // RocketReach or Thrive page), so its selectors find nothing in
                  // this app and the script throws before doing anything (no DOM
                  // change, no navigation, no stray tab).
                  e.preventDefault();
                  toast.info(inAppClickHint);
                }}
                className="inline-flex cursor-grab items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-label font-bold text-bg shadow-lg active:cursor-grabbing"
              >
                <Bookmark size={15} aria-hidden />
                {title}
              </a>
            </div>
          </section>

          {/* How to use */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">How to use</h2>
            <ol className="list-decimal space-y-1 pl-5 text-body text-text-secondary">
              {howToSteps.map((step) => (
                <li key={step.id}>{step.content}</li>
              ))}
            </ol>
          </section>

          {/* Training (optional) */}
          {training && (
            <section className="rounded-md border border-border bg-surface p-5">
              <h2 className="mb-2 text-h3 text-text">Training</h2>
              <a
                href={training.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-body text-accent hover:text-accent-hover"
              >
                <Play size={14} aria-hidden />
                {training.label}
              </a>
              {training.note && (
                <p className="mt-2 text-caption text-text-secondary">{training.note}</p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
